/*
 * PersonaClick (REES46) web-integration events for Fresh Scarfs.
 *
 * Init lives in layout/theme.liquid (window.personaclick + shop id). This file
 * fires the tracking events. It mirrors the hook points used by gtm-helpers.js
 * (fetch interception for cart, capture-phase click for wishlist) so behaviour
 * stays consistent with the existing analytics layer.
 *
 * Page-specific data (category id, search query, customer profile) is provided
 * by window.PC_DATA, emitted from theme.liquid.
 *
 * ES5 style on purpose, to match gtm-helpers.js.
 */
(function () {
  'use strict';

  var D = window.PC_DATA || {};

  // Keep a reference to the current fetch BEFORE we patch it, so our own
  // /cart.js reads do not recurse through our interceptor.
  var nativeFetch = window.fetch;

  function pc() {
    if (typeof window.personaclick === 'function') {
      window.personaclick.apply(null, arguments);
    }
  }

  function track() {
    var args = ['track'].concat(Array.prototype.slice.call(arguments));
    pc.apply(null, args);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Product view  →  track("view", { id })
  // Source attribution for clicks coming from search / recommendations.
  // ───────────────────────────────────────────────────────────────────────────
  var SOURCE_KEY = 'pc_view_source';
  var SOURCE_TTL = 5 * 60 * 1000; // 5 min

  function storeViewSource(productId, source) {
    if (!productId) return;
    try {
      sessionStorage.setItem(SOURCE_KEY, JSON.stringify({
        id: String(productId),
        from: source.from,
        code: source.code || '',
        ts: Date.now()
      }));
    } catch (e) {}
  }

  function readViewSource(productId) {
    try {
      var raw = sessionStorage.getItem(SOURCE_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      sessionStorage.removeItem(SOURCE_KEY);
      if (!s || String(s.id) !== String(productId)) return null;
      if (Date.now() - (s.ts || 0) > SOURCE_TTL) return null;
      return { from: s.from, code: s.code };
    } catch (e) { return null; }
  }

  // Capture clicks on product links inside search results / recommendation
  // blocks, so the destination product view can report its source.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href*="/products/"]');
    if (!link) return;

    var card = link.closest('[data-product_id], [data-product-id], product-card');
    var productId = '';
    if (card) {
      productId = card.dataset.product_id || card.dataset.productId || '';
    }
    if (!productId) return;

    var inSearch = link.closest('#main-search, predictive-search, [data-predictive-search], .main-search, .search-results');
    var recContainer = link.closest('[data-recommended-by], .personaclick-recommendation, [data-pc-recommendation]');

    if (recContainer) {
      storeViewSource(productId, { from: 'recommendation', code: recContainer.dataset.recommendedBy || '' });
    } else if (inSearch) {
      storeViewSource(productId, { from: 'search' });
    }
  }, true);

  function trackProductView() {
    // Only on the product detail page. (Guard prevents firing on category /
    // search pages, where the DOM fallback would otherwise pick up a product card.)
    if (D.template !== 'product') return;
    var product = D.product;
    if (!product || !product.id) {
      // Fallback: read the product id off the PDP add-to-cart wrapper.
      var el = document.querySelector('[data-product_id]');
      if (el) product = { id: el.dataset.product_id };
    }
    if (!product || !product.id) return;

    var payload = { id: String(product.id) };
    var source = readViewSource(product.id);
    if (source) {
      payload.recommended_by = source.from;
      if (source.code) payload.recommended_code = source.code;
    }
    track('view', payload);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Category view  →  track("category", "<collection id>")  (bare string per docs)
  // ───────────────────────────────────────────────────────────────────────────
  function trackCategoryView() {
    if (!D.collection || !D.collection.id) return;
    track('category', String(D.collection.id));
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Search  →  track("search", "<query>")  (bare string, complete phrase, per docs)
  // ───────────────────────────────────────────────────────────────────────────
  function trackSearch() {
    if (!D.search || !D.search.query) return;
    track('search', D.search.query);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Cart: add / update / remove.
  // Strategy: after any cart-mutating request we re-read /cart.js and send the
  // FULL current cart (amount = total quantity per product, per spec). We also
  // diff against the previous snapshot to fire remove_from_cart only when a
  // product leaves the cart entirely (not on quantity decrease).
  // ───────────────────────────────────────────────────────────────────────────
  var prevCart = null;       // map: productId -> { amount, price }
  var resyncTimer = null;

  function buildCartMap(cart) {
    var map = {};
    if (!cart || !cart.items) return map;
    cart.items.forEach(function (item) {
      var id = String(item.product_id);
      if (!map[id]) {
        map[id] = { amount: 0, price: (item.final_price || item.price || 0) / 100 };
      }
      map[id].amount += item.quantity || 0;
    });
    return map;
  }

  function cartItemsPayload(map) {
    var items = [];
    Object.keys(map).forEach(function (id) {
      items.push({ id: id, amount: map[id].amount, price: map[id].price });
    });
    return items;
  }

  // opts: { report:Boolean (send track "cart"), removals:Boolean (diff for
  // remove_from_cart) }. Seeding the snapshot on load uses report:false so we
  // only emit the cart event on an actual view or mutation.
  function syncCart(opts) {
    opts = opts || {};
    var cartUrl = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js';
    nativeFetch(cartUrl, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var newMap = buildCartMap(cart);

        if (opts.removals && prevCart) {
          Object.keys(prevCart).forEach(function (id) {
            if (!newMap[id]) {
              // Product gone entirely → genuine removal (not a qty decrease).
              // Docs: remove_from_cart takes a bare product-id string.
              track('remove_from_cart', String(id));
            }
          });
        }

        if (opts.report) {
          // PersonaClick expects the items as a bare array (not { items: [...] }).
          track('cart', cartItemsPayload(newMap));
        }
        prevCart = newMap;
      })
      .catch(function () {});
  }

  function scheduleCartSync(opts) {
    clearTimeout(resyncTimer);
    resyncTimer = setTimeout(function () { syncCart(opts); }, 350);
  }

  (function patchCartFetch() {
    var _fetch = window.fetch;
    window.fetch = function () {
      var args = Array.prototype.slice.call(arguments);
      var url = args[0];
      var options = args[1] || {};
      var urlStr = (typeof url === 'string') ? url : (url && url.url ? url.url : '');
      var method = (options.method || (url && url.method) || 'GET').toUpperCase();

      var isMutation = urlStr && method === 'POST' &&
        /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(urlStr);
      // Back-in-Stock / Price-Drop subscribe via the notify app's proxy (see the
      // Back-in-Stock section below). Fires for both guest and logged-in flows.
      var isNotifySubscribe = urlStr && method === 'POST' && NOTIFY_PROXY_RE.test(urlStr);

      if (!isMutation && !isNotifySubscribe) {
        return _fetch.apply(window, args);
      }

      var notifyBody = isNotifySubscribe && typeof options.body === 'string' ? options.body : null;

      return _fetch.apply(window, args).then(function (response) {
        if (isMutation) {
          // Re-read the authoritative cart after the mutation settles.
          scheduleCartSync({ report: true, removals: true });
        }
        if (notifyBody && response && response.ok) {
          handleNotifySubscribe(notifyBody);
        }
        return response;
      });
    };
  })();

  // Cart "view": cart page load is handled in init(); here we cover opening the
  // cart drawer from the header icon.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-drawer]')) {
      scheduleCartSync({ report: true, removals: false });
    }
  });

  // ───────────────────────────────────────────────────────────────────────────
  // Wishlist toggle  →  track("wish" | "remove_wish", { id })
  //
  // The heart is a single toggle that works site-wide (product cards, PDP,
  // favorites page). data-favorite on the theme buttons is unreliable
  // (hardcoded "false" on cards), so we read the real state from the app's
  // localStorage (`hollyWishListProducts` = [{ productId, productVariantId,
  // price }, ...]). Capture phase runs BEFORE the app toggles the state, so:
  //   already in wishlist → this click removes → remove_wish
  //   not in wishlist     → this click adds    → wish
  // Controls: .holly-wishlist-button (theme) and .wishlist_icon /
  // removeFromWishlist(...) (holly-palm-wishlist-pro app, e.g. favorites page).
  // ───────────────────────────────────────────────────────────────────────────
  function wishlistProductIds() {
    try {
      var arr = JSON.parse(localStorage.getItem('hollyWishListProducts')) || [];
      return arr.map(function (x) {
        return String(x && x.productId != null ? x.productId : x);
      });
    } catch (e) { return []; }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest(
      '.holly-wishlist-button, .wishlist_icon[data-holly_product_id], ' +
      '[onclick*="removeFromWishlist"], [onclick*="hollyWishListButton"]'
    );
    if (!btn) return;

    var productId = btn.dataset.product_id || btn.dataset.holly_product_id ||
      (btn.closest('[data-product_id]') && btn.closest('[data-product_id]').dataset.product_id) || '';
    if (!productId) {
      var m = /\(\s*['"]?(\d{6,})/.exec(btn.getAttribute('onclick') || '');
      if (m) productId = m[1];
    }
    if (!productId) return;

    // State BEFORE the toggle decides add vs remove.
    // Docs: wish / remove_wish take a bare product-id string.
    var removing = wishlistProductIds().indexOf(String(productId)) > -1;
    track(removing ? 'remove_wish' : 'wish', String(productId));
  }, true);

  // Cart remove-modal "sil ve favorilere ekle": adds the removed line's product
  // to the wishlist via the app (no .holly-wishlist-button involved), so the
  // toggle hook above never sees it. Product id comes off the cart line element,
  // resolved through the modal confirm button's item index (set by cart.js).
  // The cart removal itself is reported by the fetch interceptor's cart diff.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-remove-and-favorite');
    if (!btn) return;

    var modal = document.getElementById('CartRemoveModal');
    var confirmBtn = modal && modal.querySelector('.js-remove-confirm');
    if (!confirmBtn) return;

    var itemIndex = parseInt(confirmBtn.dataset.itemIndex, 10);
    if (isNaN(itemIndex)) return;
    var line = itemIndex + 1; // cart line ids are 1-based

    var cartItem = document.getElementById('CartDrawer-Item-' + line) ||
                   document.getElementById('CartItem-' + line);
    var productId = cartItem ? (cartItem.dataset.productId || '') : '';
    if (productId) track('wish', String(productId));
  }, true);

  // ───────────────────────────────────────────────────────────────────────────
  // User integration: profile/set, subscription manage, segment add.
  // Formats per PersonaClick JS SDK docs:
  //   personaclick('profile', 'set', { id, email, phone, first_name, last_name,
  //                                    birthday, gender('m'|'f'), ... })
  //   personaclick('subscription', 'manage', { email, phone, email_bulk,
  //                                    email_chain, email_transactional, sms_* })
  //   personaclick('segment', 'add', { segment_id, email, phone })
  // Timing (per PersonaClick review):
  //   - profile/set: every logged-in page
  //   - subscription manage: ONLY after registration + on the account page
  //   - segment add: registration → 66688, newsletter → 66689
  // ───────────────────────────────────────────────────────────────────────────
  var SEGMENT_REGISTER = 66688;   // add after registration
  var SEGMENT_NEWSLETTER = 66689; // add after newsletter signup

  // Turkish mobile → 905XXXXXXXXX (country code + 10 local digits).
  function normalizePhone(raw) {
    if (!raw) return '';
    var d = String(raw).replace(/\D/g, '');
    if (!d) return '';
    if (d.indexOf('90') === 0 && d.length >= 12) return d;
    if (d.charAt(0) === '0') d = d.slice(1);
    if (d.length === 10) return '90' + d;
    return d.indexOf('90') === 0 ? d : '90' + d;
  }

  function mapGender(g) {
    if (!g) return undefined;
    g = String(g).toLowerCase();
    if (g === 'male' || g === 'm' || g === 'erkek') return 'm';
    if (g === 'female' || g === 'f' || g === 'kadın' || g === 'kadin') return 'f';
    return undefined;
  }

  function clean(obj) {
    Object.keys(obj).forEach(function (k) {
      if (obj[k] === undefined || obj[k] === null || obj[k] === '') delete obj[k];
    });
    return obj;
  }

  // profile/set only. Fired on every logged-in page (per spec). Manage
  // Subscriptions is intentionally NOT called here — it must fire only after
  // registration and on the account page (see runUserIntegration).
  // `done` runs after the profile POST succeeds (SDK: personaclick('profile',
  // 'set', params, success, error)) — subscription manage / segment add must be
  // chained through it per PersonaClick's integration review.
  function setProfile(c, done) {
    if (!c || (!c.email && !c.phone)) return;
    var params = clean({
      id: c.id ? String(c.id) : undefined,
      email: c.email,
      phone: normalizePhone(c.phone),
      first_name: c.first_name,
      last_name: c.last_name,
      birthday: c.birthday,
      gender: mapGender(c.gender),
      call_consent: typeof c.call_consent === 'boolean' ? c.call_consent : undefined,
      // Default address city/district (PersonaClick request, custom attributes).
      // Callers that don't carry address data (e.g. the account-form update
      // event) fall back to the page context.
      il: c.il || (D.customer && D.customer.il) || undefined,
      ilce: c.ilce || (D.customer && D.customer.ilce) || undefined
    });
    mirrorProfileForCheckout(params);
    pc('profile', 'set', params, typeof done === 'function' ? done : undefined);
  }

  // Mirror the identity + address into localStorage so the checkout Web Pixel
  // (custom-events.html) can repeat the same profile/set on purchase.
  //
  // Why this exists: Shopify's checkout address has no ilce (district) field at
  // all, and this store writes the address *description* into address.city (see
  // sections/main-addresses.liquid) — so the real il/ilce cannot be recovered
  // from the checkout payload. They are only known here, from the customer's
  // hollyaccount.addresses metafield. The pixel runs in a separate sandbox
  // origin and reads this back through Shopify's browser.localStorage proxy.
  function mirrorProfileForCheckout(p) {
    try {
      localStorage.setItem('pc_profile', JSON.stringify({
        id: p.id,
        email: p.email,
        phone: p.phone,
        first_name: p.first_name,
        last_name: p.last_name,
        il: p.il,
        ilce: p.ilce
      }));
    } catch (e) {}
  }

  // bulk + chain follow the user's consent; transactional is always true.
  function manageSubscriptions(c) {
    var phone = normalizePhone(c.phone);
    var hasEmail = !!c.email;
    var hasPhone = !!phone;
    var sub = clean({ email: c.email, phone: phone });

    if (hasEmail) {
      sub.email_bulk = !!c.email_consent;
      sub.email_chain = !!c.email_consent;
      sub.email_transactional = true;
    }
    if (hasPhone) {
      sub.sms_bulk = !!c.sms_consent;
      sub.sms_chain = !!c.sms_consent;
      sub.sms_transactional = true;
    }
    if (hasEmail || hasPhone) pc('subscription', 'manage', sub);
  }

  // segment/add per docs: { segment_id, email, phone }.
  function addToSegment(segmentId, identity) {
    pc('segment', 'add', clean({
      segment_id: segmentId,
      email: identity.email,
      phone: identity.phone ? normalizePhone(identity.phone) : undefined
    }));
  }

  // The account (profile) info page renders the account-info form.
  function isAccountInfoPage() {
    return !!(document.getElementById('info_email') ||
              document.querySelector('[x-data="accountInfoPage"]'));
  }

  // Pending-intent helpers (survive the page reload after a form submit).
  function storePending(key, data) {
    try { sessionStorage.setItem(key, JSON.stringify({ data: data, ts: Date.now() })); } catch (e) {}
  }
  function readPending(key, ttl) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var p = JSON.parse(raw);
      sessionStorage.removeItem(key);
      if (ttl && Date.now() - (p.ts || 0) > ttl) return null;
      return p.data;
    } catch (e) { return null; }
  }

  // Capture email at submit time for newsletter + registration forms.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;

    var nlEmail = form.querySelector('input[name="contact[email]"]');
    var nlTag = form.querySelector('input[name="contact[tags]"]');
    if (nlEmail && nlTag && /newsletter/i.test(nlTag.value || '')) {
      storePending('pc_newsletter', { email: nlEmail.value });
    }

    // Registration email fallback (full data incl. phone is stored by
    // main-register.liquid under 'pc_register').
    var regEmail = form.querySelector('input[name="customer[email]"]');
    if (regEmail && !sessionStorage.getItem('pc_register')) {
      storePending('pc_register', { email: regEmail.value });
    }
  }, true);

  function runUserIntegration() {
    // 1. Registration: fired once the new customer is logged in. Uses the data
    //    captured at sign-up (incl. phone + consents) since the account
    //    metafields may not be populated yet on this first page load.
    var reg = readPending('pc_register', 30 * 60 * 1000);
    var isFreshRegistration = reg && D.customer && D.customer.email;

    if (isFreshRegistration) {
      var regCustomer = {
        id: D.customer.id,
        email: reg.email || D.customer.email,
        phone: reg.phone || D.customer.phone,
        first_name: reg.first_name || D.customer.first_name,
        last_name: reg.last_name || D.customer.last_name,
        gender: reg.gender || D.customer.gender,
        birthday: reg.birthday || D.customer.birthday,
        email_consent: (reg.email_consent != null) ? reg.email_consent : D.customer.email_consent,
        sms_consent: (reg.sms_consent != null) ? reg.sms_consent : D.customer.sms_consent,
        call_consent: (reg.call_consent != null) ? reg.call_consent : D.customer.call_consent
      };
      // Manage Subscriptions + segment add must run as the profile/set success
      // callback (PersonaClick review requirement), not in parallel with it.
      setProfile(regCustomer, function () {
        manageSubscriptions(regCustomer);        // register → manage subscriptions
        addToSegment(SEGMENT_REGISTER, { email: regCustomer.email, phone: regCustomer.phone });
      });
    } else if (D.customer && (D.customer.email || D.customer.phone)) {
      // 2. Logged-in on any page → profile/set. Manage Subscriptions only on
      //    the account info page (not on every page), chained as its callback.
      setProfile(D.customer, isAccountInfoPage() ? function () {
        manageSubscriptions(D.customer);
      } : undefined);
    }

    // 3. Newsletter signup success (footer + homepage + popup). Shopify appends
    //    ?customer_posted=true after a successful customer/newsletter form post.
    //    This is an email opt-in, so Manage Subscriptions fires here too.
    if (/[?&]customer_posted=true/.test(window.location.search)) {
      var nl = readPending('pc_newsletter', 30 * 60 * 1000);
      var email = (nl && nl.email) || '';
      if (email) {
        // Segment add + manage subscriptions as profile/set's success callback
        // (PersonaClick review requirement for the newsletter flow).
        pc('profile', 'set', { email: email }, function () {
          manageSubscriptions({ email: email, email_consent: true });
          addToSegment(SEGMENT_NEWSLETTER, { email: email });
        });
      }
    }
  }

  // Re-fire profile/set + manage subscriptions after an in-place account update
  // (dispatched from hp-account-customer-info.liquid). This is the account page,
  // so subscriptions belong here too, chained as the profile/set callback.
  document.addEventListener('pc:profile-updated', function (e) {
    if (e && e.detail) {
      var detail = e.detail;
      setProfile(detail, function () {
        manageSubscriptions(detail);
      });
    }
  });

  // ───────────────────────────────────────────────────────────────────────────
  // Back-in-Stock & Price-Drop subscriptions.
  //
  // The notify modal is injected by the merchant's own app (we cannot modify it),
  // and it has TWO flows:
  //   guest      → email modal → #holly-notify-btn → POST /apps/holly-notifications/Track/add
  //   logged-in  → NO modal; the app subscribes immediately with the same POST
  // A button-click hook misses the logged-in flow entirely, so we intercept the
  // app-proxy request itself (fetch is already patched below for cart mutations).
  // Request body: { productId, productVariantId, trackingType, email, ... }
  //
  //   trackingType "InStock"  → subscribe_trigger / product_available  { item, email }
  //   trackingType "Discount" → subscribe_trigger / product_price_decrease { item, price, email }
  // If the product has a size option, PersonaClick requires the selected size as
  // properties.fashion_size, in the same format as the feed (raw option value —
  // provided per-variant via PC_DATA.product.variants).
  // ───────────────────────────────────────────────────────────────────────────
  var NOTIFY_PROXY_RE = /\/apps\/holly-notifications\/Track\/add/;

  function handleNotifySubscribe(rawBody) {
    var data;
    try { data = JSON.parse(rawBody); } catch (e) { return; }
    if (!data || !data.productId || !data.email) return;

    var variants = (D.product && D.product.variants) || {};
    var variant = variants[String(data.productVariantId)] || {};

    var payload = { item: String(data.productId), email: data.email };
    if (variant.size) payload.properties = { fashion_size: String(variant.size) };

    if (data.trackingType === 'Discount') {
      // SDK rejects product_price_decrease without a price >= 1.
      var price = (variant.price != null) ? variant.price : (D.product && D.product.price);
      if (typeof price !== 'number' || isNaN(price) || price < 1) return;
      payload.price = price;
      pc('subscribe_trigger', 'product_price_decrease', payload);
    } else {
      pc('subscribe_trigger', 'product_available', payload);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Boot
  // ───────────────────────────────────────────────────────────────────────────
  function init() {
    trackProductView();
    trackCategoryView();
    trackSearch();
    // Seed the cart snapshot silently; report it as a view only on the cart page.
    syncCart({ report: D.template === 'cart', removals: false });
    runUserIntegration();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
